import { Controller } from '@hotwired/stimulus';

/*
 * One controller drives both layouts of the same markup (menu.html.twig), so
 * the server renders a single cacheable fragment. Layout is picked at event
 * time (not at connect) so a resize or rotation never needs a re-init.
 *
 * Desktop (>= 1280px, the Tailwind `desktop:` breakpoint): category panels are
 * mega-menus under the header, level panels are columns shown/hidden in place,
 * and #menu-overlay dims the page. Clicks outside the header close everything.
 *
 * Mobile: the nav is a fullscreen drawer. Navigation is a drill-down: mainList
 * slides out by 100vw while the category panel (N * 100vw wide, parked at
 * left: 100vw) slides in, then shifts by one more 100vw per level. The
 * navigationStack lets the back button undo one level at a time, and the
 * sub-header (menuHeaderSelection) replaces the main mobile header while
 * inside a category.
 */
export default class extends Controller {
    static targets = [
        'menuListWrapper',
        'mainList',
        'categoryPanel',
        'categoryButton',
        'levelPanel',
        'levelButton',
        'mobileMenuButton',
        'mobileHeader',
        'menuHeaderSelection',
        'menuHeaderSelectionName',
        'burgerIcon',
        'closeIcon',
    ];

    navigationStack = [];
    activeCategory = null;

    connect() {
        document.body.addEventListener('click', this.handleOutsideClick);
    }

    disconnect() {
        document.body.removeEventListener('click', this.handleOutsideClick);
    }

    get isDesktop() {
        return window.matchMedia('(min-width: 1280px)').matches;
    }

    findCategoryPanel(category) {
        return this.categoryPanelTargets.find(
            (panel) => panel.dataset.menuCategory === category,
        );
    }

    handleOutsideClick = (event) => {
        if (this.isDesktop && !this.element.contains(event.target)) {
            this.closeDesktopMenus();
        }
    };

    toggleMobileMenu(event) {
        event.preventDefault();

        const isOpening =
            this.menuListWrapperTarget.classList.contains('hidden');
        const flashMessage = document.getElementById('front-banner-flash');

        if (isOpening) {
            this.resetMobileNavigation();
            if (flashMessage) flashMessage.classList.add('hidden');
            this.menuListWrapperTarget.classList.remove('hidden');
            this.menuListWrapperTarget.setAttribute('aria-hidden', 'false');
            this.burgerIconTarget.hidden = true;
            this.closeIconTarget.hidden = false;
            document.body.classList.add('overflow-hidden', 'fixed');
            this.mobileHeaderTarget.classList.add(
                'fixed',
                'top-0',
                'left-0',
                'z-50',
            );
        } else {
            this.closeMobileMenu();
        }

        this.mobileMenuButtonTarget.setAttribute(
            'aria-expanded',
            String(isOpening),
        );
    }

    openCategory(event) {
        event.preventDefault();

        const { category, title } = event.params;
        const categoryPanel = this.findCategoryPanel(category);

        if (this.isDesktop) {
            const isOpen =
                categoryPanel && !categoryPanel.classList.contains('hidden');
            this.closeDesktopMenus();

            if (!categoryPanel || isOpen) return;

            this.showPanel(categoryPanel);
            event.currentTarget.classList.add('font-semibold', 'underline');
            event.currentTarget.setAttribute('aria-expanded', 'true');
            document.getElementById('menu-overlay').classList.remove('hidden');

            const defaultKey = categoryPanel.dataset.menuDefaultKey;
            if (defaultKey) {
                const defaultButton = this.levelButtonTargets.find(
                    (button) =>
                        button.dataset.menuCategoryParam === category &&
                        button.dataset.menuKeyParam === defaultKey &&
                        button.dataset.menuLevelParam === '2',
                );
                if (defaultButton) {
                    this.openDesktopLevel(
                        defaultButton,
                        category,
                        2,
                        defaultKey,
                    );
                }
            }

            return;
        }

        if (!categoryPanel) return;

        this.resetMobileNavigation();
        this.activeCategory = category;
        this.navigationStack = [{ level: 1, title }];
        this.showPanel(categoryPanel);
        // The panel must be rendered at its offscreen position before transitioning.
        void categoryPanel.offsetWidth;
        this.mainListTarget.style.transform = 'translateX(-100vw)';
        categoryPanel.style.transform = 'translateX(-100vw)';
        event.currentTarget.setAttribute('aria-expanded', 'true');
        event.currentTarget.classList.add(
            'font-semibold',
            'text-content-brand',
        );
        this.mobileHeaderTarget.classList.add('hidden');
        this.menuHeaderSelectionTarget.classList.remove('hidden');
        this.menuHeaderSelectionTarget.classList.add('flex');
        this.menuHeaderSelectionNameTarget.innerText = title;
    }

    openLevel(event) {
        event.preventDefault();

        const { category, key, level, title } = event.params;
        const numericLevel = Number(level);

        if (this.isDesktop) {
            this.openDesktopLevel(
                event.currentTarget,
                category,
                numericLevel,
                key,
            );
            return;
        }

        const targetPanel = this.findLevelPanel(category, key, numericLevel);
        const categoryPanel = this.findCategoryPanel(category);

        if (!targetPanel || !categoryPanel) return;

        this.levelPanelTargets
            .filter(
                (panel) =>
                    panel.dataset.menuCategory === category &&
                    Number(panel.dataset.menuLevel) >= numericLevel,
            )
            .forEach((panel) => this.hidePanel(panel));
        this.levelButtonTargets
            .filter(
                (button) =>
                    button.dataset.menuCategoryParam === category &&
                    Number(button.dataset.menuLevelParam) >= numericLevel,
            )
            .forEach((button) => button.removeAttribute('data-active'));

        this.showPanel(targetPanel);
        event.currentTarget.setAttribute('data-active', '');
        event.currentTarget.classList.add(
            'font-semibold',
            'text-content-brand',
        );
        this.navigationStack = this.navigationStack.slice(0, numericLevel - 1);
        this.navigationStack.push({ level: numericLevel, key, title });
        categoryPanel.style.transform = `translateX(calc(-100vw * ${numericLevel}))`;
        this.menuHeaderSelectionNameTarget.innerText = title;
    }

    goBack() {
        const current = this.navigationStack.pop();
        if (!current) return;

        if (current.level === 1) {
            const categoryPanel = this.findCategoryPanel(this.activeCategory);
            if (categoryPanel) this.hidePanel(categoryPanel);
            this.mainListTarget.style.transform = '';
            this.activeCategory = null;
            this.mobileHeaderTarget.classList.remove('hidden');
            this.menuHeaderSelectionTarget.classList.add('hidden');
            this.menuHeaderSelectionTarget.classList.remove('flex');
            this.menuHeaderSelectionNameTarget.innerText = '';
            this.categoryButtonTargets.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
                button.classList.remove('text-content-brand');
            });
            return;
        }

        const currentPanel = this.findLevelPanel(
            this.activeCategory,
            current.key,
            current.level,
        );
        if (currentPanel) this.hidePanel(currentPanel);

        const currentButton = this.levelButtonTargets.find(
            (button) =>
                button.dataset.menuCategoryParam === this.activeCategory &&
                button.dataset.menuKeyParam === current.key &&
                Number(button.dataset.menuLevelParam) === current.level,
        );
        if (currentButton) {
            currentButton.removeAttribute('data-active');
            currentButton.classList.remove(
                'font-semibold',
                'text-content-brand',
            );
        }

        const previous = this.navigationStack[this.navigationStack.length - 1];
        const categoryPanel = this.findCategoryPanel(this.activeCategory);
        if (categoryPanel) {
            categoryPanel.style.transform = `translateX(calc(-100vw * ${previous.level}))`;
        }
        this.menuHeaderSelectionNameTarget.innerText = previous.title;
    }

    closeMobileMenu() {
        this.resetMobileNavigation();
        this.menuListWrapperTarget.classList.add('hidden');
        this.menuListWrapperTarget.setAttribute('aria-hidden', 'true');
        this.burgerIconTarget.hidden = false;
        this.closeIconTarget.hidden = true;
        this.mobileMenuButtonTarget.setAttribute('aria-expanded', 'false');
        this.mobileHeaderTarget.classList.remove(
            'fixed',
            'top-0',
            'left-0',
            'z-50',
        );
        document.body.classList.remove('overflow-hidden', 'fixed');

        const flashMessage = document.getElementById('front-banner-flash');
        if (flashMessage) flashMessage.classList.remove('hidden');
    }

    resetMobileNavigation() {
        this.resetPanels();
        this.mainListTarget.style.transform = '';
        this.activeCategory = null;
        this.mobileHeaderTarget.classList.remove('hidden');
        this.menuHeaderSelectionTarget.classList.add('hidden');
        this.menuHeaderSelectionTarget.classList.remove('flex');
        this.menuHeaderSelectionNameTarget.innerText = '';
    }

    openDesktopLevel(button, category, level, key) {
        const isOpen = button.hasAttribute('data-active');

        this.levelPanelTargets
            .filter(
                (panel) =>
                    panel.dataset.menuCategory === category &&
                    Number(panel.dataset.menuLevel) >= level,
            )
            .forEach((panel) => this.hidePanel(panel));
        this.levelButtonTargets
            .filter(
                (candidate) =>
                    candidate.dataset.menuCategoryParam === category &&
                    Number(candidate.dataset.menuLevelParam) >= level,
            )
            .forEach((candidate) => {
                candidate.removeAttribute('data-active');
                candidate.classList.remove(
                    'font-semibold',
                    'text-content-brand',
                );
            });

        if (isOpen) return;

        const targetPanel = this.findLevelPanel(category, key, level);
        if (!targetPanel) return;

        this.showPanel(targetPanel);
        button.setAttribute('data-active', '');
        button.classList.add('font-semibold', 'text-content-brand');
    }

    findLevelPanel(category, key, level) {
        return this.levelPanelTargets.find(
            (panel) =>
                panel.dataset.menuCategory === category &&
                panel.dataset.menuKey === key &&
                Number(panel.dataset.menuLevel) === level,
        );
    }

    showPanel(panel) {
        panel.classList.remove('hidden');
        panel.classList.add('flex');
    }

    hidePanel(panel) {
        panel.classList.add('hidden');
        panel.classList.remove('flex');
    }

    closeDesktopMenus() {
        this.resetPanels();
        document.getElementById('menu-overlay').classList.add('hidden');
    }

    resetPanels() {
        this.categoryPanelTargets.forEach((panel) => {
            panel.style.transform = '';
            this.hidePanel(panel);
        });
        this.levelPanelTargets.forEach((panel) => this.hidePanel(panel));
        this.categoryButtonTargets.forEach((button) => {
            button.setAttribute('aria-expanded', 'false');
            button.classList.remove('text-content-brand', 'underline');
        });
        this.levelButtonTargets.forEach((button) => {
            button.removeAttribute('data-active');
            button.classList.remove('font-semibold', 'text-content-brand');
        });
    }
}
